/**
 * SimulationResilienceSection — the "Simulation & Resilience" appendix
 * subsection (wave R3, R3-C1). Renders the report's `simEvidenceExhibits`
 * (reliability N+1, ride-through transients, and B7-gated optimisation studies)
 * as cited figures + tables, folded INTO the Appendix & References section
 * exactly like `ExtendedDataSection` (nested `data-section="simulationResilience"`,
 * a small-caps `appendixHeadStyle` sub-head). Data-gated: `return null` when the
 * report carries no exhibits, so the section is absent (no empty section).
 *
 * CITATION GOVERNANCE: an exhibit renders as a CITED figure ONLY when
 * `isExhibitCitable(exhibit)` is true — i.e. the stored exhibit carries the
 * product citation policy stamp (`citationPolicyId`) with `citable: true`,
 * decided at write time from fail-closed server verification. A pre-policy
 * (unstamped) or non-citable exhibit renders under the `B7_EXPLORATORY_CAPTION`
 * watermark and is never presented as a citable result.
 *
 * All figures are PURE INLINE SVG (no Recharts, no canvas, no external assets) —
 * the `EditorialRadarPanel` treatment — so they are byte-identical on-screen and
 * in the Paged.js / Puppeteer PDF export.
 */
import React from 'react';
import type { ReportTheme } from '../../../types/reportTheme';
import type { SiteRagReport } from '../../../types/siteRag';
import { roleColor, roleColorAlpha } from '../../../lib/reportRoleColors';
import { EditableHeading } from './EditableHeading';
import { ReportSourceNote } from './ReportSourceNote';
import { appendixHeadStyle } from './ReferencesSection';
import {
  type SimEvidenceExhibit,
  type ReliabilityNplus1Exhibit,
  type ScenarioRideThroughExhibit,
  type StudyExhibit,
  resolveSimEvidenceExhibits,
  isExhibitCitable,
  exhibitTitle,
  exhibitCitationLine,
  B7_EXPLORATORY_CAPTION,
  RELIABILITY_TABLE_COLUMNS,
  reliabilityTableRows,
  reliabilityConfigRows,
  rideThroughKpiRows,
  rideThroughBreachesSla,
  readSweep2d,
  readPareto,
  readMonteCarlo,
  readOptimizer,
} from '../../../lib/reportGeneration/evidence/simEvidenceExhibit';

const MONO = 'var(--report-body-font, inherit)';

export interface SimulationResilienceSectionProps {
  report: Pick<SiteRagReport, 'simEvidenceExhibits'>;
  theme: ReportTheme;
  isEditing?: boolean;
  headingOverrides?: Record<string, string>;
  setHeading?: (key: string, value: string) => void;
  /** Appendix-subsection mode (the only mode wired today). Accepted for parity
   *  with `ExtendedDataSection`; output is the folded subsection either way. */
  renderAsAppendixSubsection?: boolean;
}

// ── shared SVG scale helpers (pure) ─────────────────────────────────────────

interface Scale {
  min: number;
  max: number;
}
function extent(values: number[]): Scale {
  const finite = values.filter((v) => Number.isFinite(v));
  if (finite.length === 0) return { min: 0, max: 1 };
  let min = Math.min(...finite);
  let max = Math.max(...finite);
  if (min === max) {
    min -= 1;
    max += 1;
  }
  return { min, max };
}
/** Map value → pixel across [0,px] for an ascending scale. */
function project(v: number, s: Scale, px: number): number {
  return ((v - s.min) / (s.max - s.min)) * px;
}
function fmt(n: number, digits = 2): string {
  if (!Number.isFinite(n)) return '–';
  return Number(n.toFixed(digits)).toString();
}

// ── figure frame (title + governance band + citation) ───────────────────────

function ExhibitFigure({
  exhibit,
  theme,
}: {
  exhibit: SimEvidenceExhibit;
  theme: ReportTheme;
}): React.ReactElement {
  const citable = isExhibitCitable(exhibit);
  const hair = roleColor(theme, 'hair');
  const ink = roleColor(theme, 'ink');
  const warning = roleColor(theme, 'warning');

  const citation = exhibitCitationLine(exhibit);

  return (
    <figure
      data-testid="sim-exhibit"
      data-exhibit-kind={exhibit.kind}
      data-exhibit-citable={citable ? 'true' : 'false'}
      style={{
        margin: 0,
        padding: '10px 12px',
        border: `1px solid ${hair}`,
        breakInside: 'avoid',
      }}
    >
      <figcaption
        style={{
          fontFamily: MONO,
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: '0.02em',
          color: ink,
          marginBottom: 8,
        }}
      >
        {exhibitTitle(exhibit)}
      </figcaption>

      {/* Governance — exploratory watermark for any exhibit the product
          citation policy did not stamp citable (pre-policy documents included). */}
      {!citable && (
        <div
          data-testid="b7-watermark"
          data-b7-exploratory="true"
          style={{
            fontFamily: MONO,
            fontSize: 10.5,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: warning,
            background: roleColorAlpha(theme, 'warning', 0.1),
            border: `1px solid ${roleColorAlpha(theme, 'warning', 0.4)}`,
            padding: '4px 8px',
            marginBottom: 8,
          }}
        >
          {B7_EXPLORATORY_CAPTION}
        </div>
      )}

      <ExhibitBody exhibit={exhibit} theme={theme} />

      {/* Footnote (study payloads may carry one). */}
      {isStudy(exhibit) && exhibit.footnote && (
        <ReportSourceNote className="mt-1.5 italic">
          {exhibit.footnote}
        </ReportSourceNote>
      )}

      {/* Citation line — only for citable exhibits. */}
      {citable && citation && (
        <ReportSourceNote className="mt-1.5">{citation}</ReportSourceNote>
      )}
    </figure>
  );
}

function isStudy(e: SimEvidenceExhibit): e is StudyExhibit {
  return e.kind.startsWith('study-');
}

function ExhibitBody({
  exhibit,
  theme,
}: {
  exhibit: SimEvidenceExhibit;
  theme: ReportTheme;
}): React.ReactElement {
  switch (exhibit.kind) {
    case 'reliability-nplus1':
      return <ReliabilityTable exhibit={exhibit} theme={theme} />;
    case 'scenario-ridethrough':
      return <RideThroughFigure exhibit={exhibit} theme={theme} />;
    case 'study-sweep2d':
      return <Sweep2dHeatmap exhibit={exhibit} theme={theme} />;
    case 'study-pareto':
      return <ParetoScatter exhibit={exhibit} theme={theme} />;
    case 'study-montecarlo':
      return <MonteCarloHistogram exhibit={exhibit} theme={theme} />;
    case 'study-optimizer':
      return <OptimizerTrace exhibit={exhibit} theme={theme} />;
    default: {
      const _exhaustive: never = exhibit;
      void _exhaustive;
      return <></>;
    }
  }
}

// ── reliability → the N/N+1/N+2 availability + nines + EUE table ─────────────

function ReliabilityTable({
  exhibit,
  theme,
}: {
  exhibit: ReliabilityNplus1Exhibit;
  theme: ReportTheme;
}): React.ReactElement {
  const rows = reliabilityTableRows(exhibit);
  const configRows = reliabilityConfigRows(exhibit);
  const hair = roleColor(theme, 'hair');
  const rule = roleColor(theme, 'rule');
  const muted = roleColor(theme, 'muted');
  const ink = roleColor(theme, 'ink');
  const accentSoft = roleColor(theme, 'accentSoft');

  return (
    <table
      style={{
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: 11,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      <thead>
        <tr>
          {RELIABILITY_TABLE_COLUMNS.map((c) => (
            <th
              key={c}
              style={{
                textAlign: c === 'Configuration' ? 'left' : 'right',
                fontFamily: MONO,
                fontSize: 9.5,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: muted,
                borderBottom: `1.5px solid ${rule}`,
                padding: '4px 8px',
              }}
            >
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((cells, ri) => {
          // Highlight the headline / recommended configuration row.
          const isHeadline = configRows[ri]?.config === exhibit.config;
          return (
            <tr key={ri} style={isHeadline ? { background: accentSoft } : undefined}>
              {cells.map((cell, ci) => (
                <td
                  key={ci}
                  style={{
                    textAlign: ci === 0 ? 'left' : 'right',
                    color: ci === 0 ? ink : roleColor(theme, 'body'),
                    fontWeight: ci === 0 ? 700 : 400,
                    borderBottom: `1px solid ${hair}`,
                    padding: '4px 8px',
                  }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ── ride-through → inlet-T-vs-time chart + minutes-to-SLA KPI callout ────────

function RideThroughFigure({
  exhibit,
  theme,
}: {
  exhibit: ScenarioRideThroughExhibit;
  theme: ReportTheme;
}): React.ReactElement | null {
  const timeline = exhibit.timeline.filter(
    (p) => Number.isFinite(p.t) && Number.isFinite(p.inletT_C),
  );
  const kpiRows = rideThroughKpiRows(exhibit);
  const breaches = rideThroughBreachesSla(exhibit);
  const accent = roleColor(theme, 'accent');
  const negative = roleColor(theme, 'negative');
  const positive = roleColor(theme, 'positive');
  const muted = roleColor(theme, 'muted');
  const hair = roleColor(theme, 'hair');
  const rule = roleColor(theme, 'rule');
  const body = roleColor(theme, 'body');
  const toneColor = breaches ? negative : positive;

  const W = 520;
  const H = 220;
  const ml = 46;
  const mr = 14;
  const mt = 12;
  const mb = 30;
  const plotW = W - ml - mr;
  const plotH = H - mt - mb;

  const chart =
    timeline.length >= 2
      ? (() => {
          const xs = extent(timeline.map((p) => p.t));
          const ys = extent([
            ...timeline.map((p) => p.inletT_C),
            exhibit.kpis.slaLimit_C,
          ]);
          const px = (t: number) => ml + project(t, xs, plotW);
          const py = (temp: number) => mt + plotH - project(temp, ys, plotH);
          const line = timeline
            .map((p) => `${px(p.t).toFixed(1)},${py(p.inletT_C).toFixed(1)}`)
            .join(' ');
          const slaY = py(exhibit.kpis.slaLimit_C);
          return (
            <svg
              viewBox={`0 0 ${W} ${H}`}
              width="100%"
              role="img"
              aria-label={`Inlet temperature vs time – ${exhibit.scenario}`}
              style={{ maxWidth: W }}
            >
              {/* axes */}
              <line x1={ml} y1={mt} x2={ml} y2={mt + plotH} stroke={rule} strokeWidth={1} />
              <line
                x1={ml}
                y1={mt + plotH}
                x2={ml + plotW}
                y2={mt + plotH}
                stroke={rule}
                strokeWidth={1}
              />
              {/* SLA limit line */}
              <line
                x1={ml}
                y1={slaY}
                x2={ml + plotW}
                y2={slaY}
                stroke={negative}
                strokeWidth={1}
                strokeDasharray="4 3"
              />
              <text x={ml + plotW} y={slaY - 3} textAnchor="end" fontSize={9} fill={negative} fontFamily={MONO}>
                {`SLA ${exhibit.kpis.slaLimit_C.toFixed(0)}°C`}
              </text>
              {/* event markers */}
              {timeline
                .filter((p) => p.event)
                .map((p, i) => (
                  <g key={i}>
                    <line
                      x1={px(p.t)}
                      y1={mt}
                      x2={px(p.t)}
                      y2={mt + plotH}
                      stroke={hair}
                      strokeWidth={1}
                      strokeDasharray="2 2"
                    />
                    <text
                      x={px(p.t) + 2}
                      y={mt + 8}
                      fontSize={8}
                      fill={muted}
                      fontFamily={MONO}
                    >
                      {p.event}
                    </text>
                  </g>
                ))}
              {/* inlet-T trace */}
              <polyline points={line} fill="none" stroke={accent} strokeWidth={1.75} strokeLinejoin="round" />
              {/* y ticks (min / max) */}
              <text x={ml - 4} y={mt + 4} textAnchor="end" fontSize={9} fill={muted} fontFamily={MONO}>
                {ys.max.toFixed(0)}
              </text>
              <text x={ml - 4} y={mt + plotH} textAnchor="end" fontSize={9} fill={muted} fontFamily={MONO}>
                {ys.min.toFixed(0)}
              </text>
              {/* x ticks (0 / max) */}
              <text x={ml} y={mt + plotH + 14} textAnchor="start" fontSize={9} fill={muted} fontFamily={MONO}>
                0s
              </text>
              <text x={ml + plotW} y={mt + plotH + 14} textAnchor="end" fontSize={9} fill={muted} fontFamily={MONO}>
                {`${xs.max.toFixed(0)}s`}
              </text>
              <text
                x={ml + plotW / 2}
                y={mt + plotH + 26}
                textAnchor="middle"
                fontSize={9}
                fill={muted}
                fontFamily={MONO}
              >
                inlet temperature (°C) vs time (s)
              </text>
            </svg>
          );
        })()
      : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {chart}
      {/* minutes-to-SLA-breach KPI callout */}
      <div
        data-testid="ride-through-kpi"
        style={{
          border: `1px solid ${hair}`,
          borderLeft: `3px solid ${toneColor}`,
          padding: '6px 10px',
        }}
      >
        <div
          style={{
            fontFamily: MONO,
            fontSize: 9.5,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: toneColor,
            fontWeight: 700,
            marginBottom: 4,
          }}
        >
          {breaches ? 'SLA breach – ride-through insufficient' : 'SLA held – ride-through adequate'}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '2px 12px' }}>
          {kpiRows.map(([label, value], i) => (
            <React.Fragment key={i}>
              <span style={{ fontSize: 10.5, color: muted }}>{label}</span>
              <span
                style={{
                  fontSize: 10.5,
                  color: body,
                  textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {value}
              </span>
            </React.Fragment>
          ))}
        </div>
      </div>
      {exhibit.narrative && (
        <p style={{ fontSize: 11, color: body, margin: 0, lineHeight: 1.4 }}>{exhibit.narrative}</p>
      )}
    </div>
  );
}

// ── study-sweep2d → contour/heatmap (failed cells hatched) ──────────────────

function Sweep2dHeatmap({
  exhibit,
  theme,
}: {
  exhibit: StudyExhibit;
  theme: ReportTheme;
}): React.ReactElement | null {
  const s = readSweep2d(exhibit.data);
  if (!s) return <StudyDataUnavailable theme={theme} />;
  const muted = roleColor(theme, 'muted');
  const rule = roleColor(theme, 'rule');
  const accent = roleColor(theme, 'accent');

  const flat = s.z.flat().filter((v) => Number.isFinite(v));
  const vs = extent(flat);
  const cols = s.xValues.length;
  const rowsN = s.yValues.length;
  const cell = 28;
  const ml = 44;
  const mt = 8;
  const mb = 26;
  const W = ml + cols * cell + 8;
  const H = mt + rowsN * cell + mb;
  const hatchId = `hatch-${exhibit.kind}-${Math.round(vs.min)}-${cols}x${rowsN}`;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      role="img"
      aria-label={`${s.xLabel} × ${s.yLabel} parameter sweep`}
      style={{ maxWidth: W }}
    >
      <defs>
        <pattern id={hatchId} width={5} height={5} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect width={5} height={5} fill={roleColor(theme, 'subtleBg')} />
          <line x1={0} y1={0} x2={0} y2={5} stroke={muted} strokeWidth={1} />
        </pattern>
      </defs>
      {s.z.map((zrow, yi) =>
        s.xValues.map((_, xi) => {
          const v = zrow[xi];
          const failed = s.failed[yi]?.[xi] === true;
          const norm = v != null && Number.isFinite(v) ? (v - vs.min) / (vs.max - vs.min) : 0;
          const x = ml + xi * cell;
          // Rows are drawn top-down; yi=0 at top.
          const y = mt + yi * cell;
          return (
            <rect
              key={`${xi}-${yi}`}
              x={x}
              y={y}
              width={cell - 1}
              height={cell - 1}
              fill={failed ? `url(#${hatchId})` : roleColorAlpha(theme, 'accent', 0.15 + 0.8 * norm)}
              stroke={rule}
              strokeWidth={0.5}
              data-failed={failed ? 'true' : undefined}
            />
          );
        }),
      )}
      {/* axis labels */}
      <text x={ml + (cols * cell) / 2} y={H - 6} textAnchor="middle" fontSize={9} fill={muted} fontFamily={MONO}>
        {s.xLabel}
      </text>
      <text
        x={12}
        y={mt + (rowsN * cell) / 2}
        textAnchor="middle"
        fontSize={9}
        fill={muted}
        fontFamily={MONO}
        transform={`rotate(-90 12 ${mt + (rowsN * cell) / 2})`}
      >
        {s.yLabel}
      </text>
      {/* legend swatch */}
      <rect x={ml} y={mt + rowsN * cell + 4} width={10} height={10} fill={accent} />
      <text x={ml + 14} y={mt + rowsN * cell + 13} fontSize={8} fill={muted} fontFamily={MONO}>
        {s.valueLabel ?? 'high'}
      </text>
    </svg>
  );
}

// ── study-pareto → 2-objective scatter with front highlighted ───────────────

function ParetoScatter({
  exhibit,
  theme,
}: {
  exhibit: StudyExhibit;
  theme: ReportTheme;
}): React.ReactElement | null {
  const p = readPareto(exhibit.data);
  if (!p) return <StudyDataUnavailable theme={theme} />;
  const accent = roleColor(theme, 'accent');
  const muted = roleColor(theme, 'muted');
  const rule = roleColor(theme, 'rule');

  const W = 420;
  const H = 260;
  const ml = 44;
  const mr = 12;
  const mt = 12;
  const mb = 34;
  const plotW = W - ml - mr;
  const plotH = H - mt - mb;
  const xs = extent(p.points.map((pt) => pt.x));
  const ys = extent(p.points.map((pt) => pt.y));
  const px = (x: number) => ml + project(x, xs, plotW);
  const py = (y: number) => mt + plotH - project(y, ys, plotH);
  const front = p.points
    .filter((pt) => pt.onFront)
    .sort((a, b) => a.x - b.x);
  const frontLine = front.map((pt) => `${px(pt.x).toFixed(1)},${py(pt.y).toFixed(1)}`).join(' ');

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Pareto front scatter" style={{ maxWidth: W }}>
      <line x1={ml} y1={mt} x2={ml} y2={mt + plotH} stroke={rule} strokeWidth={1} />
      <line x1={ml} y1={mt + plotH} x2={ml + plotW} y2={mt + plotH} stroke={rule} strokeWidth={1} />
      {front.length >= 2 && (
        <polyline points={frontLine} fill="none" stroke={accent} strokeWidth={1.25} strokeDasharray="5 3" />
      )}
      {p.points.map((pt, i) =>
        pt.onFront ? (
          <circle key={i} cx={px(pt.x)} cy={py(pt.y)} r={4} fill={accent} />
        ) : (
          <circle key={i} cx={px(pt.x)} cy={py(pt.y)} r={2.5} fill="none" stroke={muted} strokeWidth={1} />
        ),
      )}
      <text x={ml + plotW / 2} y={H - 8} textAnchor="middle" fontSize={9} fill={muted} fontFamily={MONO}>
        {p.xLabel}
      </text>
      <text
        x={12}
        y={mt + plotH / 2}
        textAnchor="middle"
        fontSize={9}
        fill={muted}
        fontFamily={MONO}
        transform={`rotate(-90 12 ${mt + plotH / 2})`}
      >
        {p.yLabel}
      </text>
    </svg>
  );
}

// ── study-montecarlo → histogram + P5/P50/P95 ───────────────────────────────

function MonteCarloHistogram({
  exhibit,
  theme,
}: {
  exhibit: StudyExhibit;
  theme: ReportTheme;
}): React.ReactElement | null {
  const m = readMonteCarlo(exhibit.data);
  if (!m) return <StudyDataUnavailable theme={theme} />;
  const accent = roleColor(theme, 'accent');
  const muted = roleColor(theme, 'muted');
  const rule = roleColor(theme, 'rule');
  const ink = roleColor(theme, 'ink');

  const W = 460;
  const H = 240;
  const ml = 40;
  const mr = 12;
  const mt = 14;
  const mb = 40;
  const plotW = W - ml - mr;
  const plotH = H - mt - mb;
  const xMin = Math.min(...m.bins.map((b) => b.x0), m.p5);
  const xMax = Math.max(...m.bins.map((b) => b.x1), m.p95);
  const xs: Scale = { min: xMin, max: xMax === xMin ? xMin + 1 : xMax };
  const countMax = Math.max(...m.bins.map((b) => b.count), 1);
  const px = (x: number) => ml + project(x, xs, plotW);
  const barH = (c: number) => (c / countMax) * plotH;
  const pLine = (x: number, color: string, label: string, dash?: string) => (
    <g>
      <line x1={px(x)} y1={mt} x2={px(x)} y2={mt + plotH} stroke={color} strokeWidth={1.25} strokeDasharray={dash} />
      <text x={px(x)} y={mt - 3} textAnchor="middle" fontSize={8.5} fill={color} fontFamily={MONO}>
        {label}
      </text>
    </g>
  );

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={`${m.label} distribution`} style={{ maxWidth: W }}>
      <line x1={ml} y1={mt + plotH} x2={ml + plotW} y2={mt + plotH} stroke={rule} strokeWidth={1} />
      {m.bins.map((b, i) => {
        const x0 = px(b.x0);
        const x1 = px(b.x1);
        const w = Math.max(1, x1 - x0 - 1);
        const h = barH(b.count);
        return (
          <rect
            key={i}
            x={x0}
            y={mt + plotH - h}
            width={w}
            height={h}
            fill={roleColorAlpha(theme, 'accent', 0.55)}
            stroke={accent}
            strokeWidth={0.5}
          />
        );
      })}
      {pLine(m.p5, muted, 'P5', '3 2')}
      {pLine(m.p50, ink, 'P50')}
      {pLine(m.p95, muted, 'P95', '3 2')}
      <text x={ml} y={H - 22} textAnchor="start" fontSize={9} fill={muted} fontFamily={MONO}>
        {fmt(xs.min)}
      </text>
      <text x={ml + plotW} y={H - 22} textAnchor="end" fontSize={9} fill={muted} fontFamily={MONO}>
        {fmt(xs.max)}
      </text>
      <text x={ml + plotW / 2} y={H - 8} textAnchor="middle" fontSize={9} fill={muted} fontFamily={MONO}>
        {m.label}
        {m.unit ? ` (${m.unit})` : ''}
      </text>
    </svg>
  );
}

// ── study-optimizer → best-point + convergence trace ────────────────────────

function OptimizerTrace({
  exhibit,
  theme,
}: {
  exhibit: StudyExhibit;
  theme: ReportTheme;
}): React.ReactElement | null {
  const o = readOptimizer(exhibit.data);
  if (!o) return <StudyDataUnavailable theme={theme} />;
  const accent = roleColor(theme, 'accent');
  const muted = roleColor(theme, 'muted');
  const rule = roleColor(theme, 'rule');
  const body = roleColor(theme, 'body');
  const positive = roleColor(theme, 'positive');

  const W = 460;
  const H = 200;
  const ml = 44;
  const mr = 12;
  const mt = 12;
  const mb = 28;
  const plotW = W - ml - mr;
  const plotH = H - mt - mb;

  const chart =
    o.trace.length >= 2
      ? (() => {
          const xs = extent(o.trace.map((t) => t.iter));
          const ys = extent(o.trace.map((t) => t.value));
          const px = (i: number) => ml + project(i, xs, plotW);
          const py = (v: number) => mt + plotH - project(v, ys, plotH);
          const line = o.trace.map((t) => `${px(t.iter).toFixed(1)},${py(t.value).toFixed(1)}`).join(' ');
          const best = o.trace[o.trace.length - 1]!;
          return (
            <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Optimizer convergence trace" style={{ maxWidth: W }}>
              <line x1={ml} y1={mt} x2={ml} y2={mt + plotH} stroke={rule} strokeWidth={1} />
              <line x1={ml} y1={mt + plotH} x2={ml + plotW} y2={mt + plotH} stroke={rule} strokeWidth={1} />
              <polyline points={line} fill="none" stroke={accent} strokeWidth={1.5} strokeLinejoin="round" />
              <circle cx={px(best.iter)} cy={py(best.value)} r={3.5} fill={positive} />
              <text x={ml} y={mt + plotH + 14} textAnchor="start" fontSize={9} fill={muted} fontFamily={MONO}>
                iter 0
              </text>
              <text x={ml + plotW} y={mt + plotH + 14} textAnchor="end" fontSize={9} fill={muted} fontFamily={MONO}>
                {`iter ${xs.max.toFixed(0)}`}
              </text>
              <text x={ml + plotW / 2} y={mt + plotH + 24} textAnchor="middle" fontSize={9} fill={muted} fontFamily={MONO}>
                {o.objectiveLabel}
              </text>
            </svg>
          );
        })()
      : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {chart}
      {o.best.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '2px 12px' }}>
          <span style={{ fontFamily: MONO, fontSize: 9, textTransform: 'uppercase', color: muted, gridColumn: '1 / -1' }}>
            Best point
          </span>
          {o.best.map((b, i) => (
            <React.Fragment key={i}>
              <span style={{ fontSize: 10.5, color: muted }}>{b.label}</span>
              <span style={{ fontSize: 10.5, color: body, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {b.value}
              </span>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

function StudyDataUnavailable({ theme }: { theme: ReportTheme }): React.ReactElement {
  return (
    <div style={{ fontSize: 11, fontStyle: 'italic', color: roleColor(theme, 'muted') }}>
      Study payload unavailable – re-run to refresh.
    </div>
  );
}

// ── section shell (appendix subsection) ─────────────────────────────────────

export function SimulationResilienceSection({
  report,
  theme,
  isEditing = false,
  headingOverrides = {},
  setHeading = () => {},
}: SimulationResilienceSectionProps): React.ReactElement | null {
  const exhibits = resolveSimEvidenceExhibits(report);
  // Data-gated: absent when there is no evidence (no empty section).
  if (exhibits.length === 0) return null;

  return (
    <div
      id="simulationResilience"
      data-section="simulationResilience"
      data-section-id="simulationResilience"
      data-subsection="simulationResilience"
      data-testid="simulation-resilience-appendix-subsection"
      className="space-y-4"
      style={{ scrollMarginTop: '80px' }}
    >
      <h3 className="text-sm font-bold uppercase tracking-widest" style={appendixHeadStyle(theme)}>
        <EditableHeading
          headingKey="simulationResilience"
          defaultText="Simulation & Resilience"
          isEditing={isEditing}
          overrides={headingOverrides}
          onChange={setHeading}
          inline
        />
      </h3>
      {exhibits.map((exhibit, i) => (
        <ExhibitFigure key={i} exhibit={exhibit} theme={theme} />
      ))}
    </div>
  );
}

export default SimulationResilienceSection;
